import api from './api';
import type { AlarmFilter, AlarmPage, Alarm } from '../types/alarm';
import type { AlarmStats } from '../types/device';

export async function getAlarms(filter: AlarmFilter): Promise<AlarmPage> {
  const params: Record<string, string> = {
    page: String(filter.page),
    size: String(filter.size),
  };
  if (filter.severity) params.severity = filter.severity;
  if (filter.device_type) params.device_type = filter.device_type;
  if (filter.alarm_type) params.alarm_type = filter.alarm_type;
  if (filter.acknowledged !== undefined) params.acknowledged = String(filter.acknowledged);
  if (filter.escalated_only) params.escalated_only = 'true';
  if (filter.start) params.start = filter.start;
  if (filter.end) params.end = filter.end;
  if (filter.excluded_types) params.excluded_types = filter.excluded_types;
  if (filter.hide_maintenance) params.hide_maintenance = 'true';

  const { data } = await api.get('/alarms/', { params });
  return data.data;
}

export interface AlarmOccurrence { occurred_at: string; }

/** 單筆警報的逐次發生紀錄（去重列每次重複發生各一筆；僅功能上線後記錄）。 */
export async function getAlarmOccurrences(alarmId: number, limit = 1000):
  Promise<{ items: AlarmOccurrence[]; total: number }> {
  const { data } = await api.get(`/alarms/${alarmId}/occurrences`, { params: { limit } });
  return data.data;
}

export async function getAlarmStats(excludedTypes?: string): Promise<AlarmStats> {
  const params: Record<string, string> = {};
  if (excludedTypes) params.excluded_types = excludedTypes;
  const { data } = await api.get('/alarms/stats', { params });
  return data.data;
}

export async function getActiveAlarms(): Promise<Alarm[]> {
  const { data } = await api.get('/alarms/active');
  return data.data;
}

export async function acknowledgeAlarm(alarmId: number): Promise<void> {
  await api.post(`/alarms/${alarmId}/acknowledge`);
}

export async function acknowledgeAlarmsBatch(alarmIds: number[]): Promise<void> {
  await api.post('/alarms/acknowledge-batch', { alarm_ids: alarmIds });
}

export async function acknowledgeAllAlarms(): Promise<number> {
  const { data } = await api.post('/alarms/acknowledge-all');
  return data.data.affected;
}

export async function deleteAllAlarms(signature: { password: string; comment: string }): Promise<number> {
  // 高影響操作：帶電子簽章（後端重新驗證密碼）
  const { data } = await api.delete('/alarms/delete-all', { data: { signature } });
  return data.data.affected;
}

export async function restoreAllAlarms(): Promise<string> {
  const { data } = await api.post('/alarms/restore-all');
  return data.data.message;
}

export interface AlarmTrendData {
  daily_by_severity: Array<{ date: string; CRITICAL: number; ALARM: number; WARNING: number; INFO: number }>;
  daily_by_device: Array<Record<string, string | number>>;
  by_severity: Array<{ name: string; value: number }>;
  by_device_type: Array<{ name: string; value: number }>;
  hourly_distribution: Array<{ hour: number; count: number }>;
  top_alarm_types: Array<{ name: string; value: number }>;
  total: number;
  days: number;
}

export async function getAlarmTrend(days: number = 30, excludedTypes?: string): Promise<AlarmTrendData> {
  const params: Record<string, string | number> = { days };
  if (excludedTypes) params.excluded_types = excludedTypes;
  const { data } = await api.get('/alarms/trend', { params });
  return data.data;
}
